import React, { useEffect, useState } from 'react';
import AppIcon from '../../components/AppIcon';
import client from '../../api/client';
import { Gift, Plus, Trash2, Link2, DollarSign, Heart, Loader2, ExternalLink } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import PayoutSetupCard from '../../components/registry/PayoutSetupCard';
import GroupGiftsEditor from '../../components/registry/GroupGiftsEditor';
import ContributionsList from '../../components/registry/ContributionsList';
import PhotoUploader from '../../components/PhotoUploader';

// Registry aggregation editor — external links + cash-fund cards +
// Venmo/PayPal/Cash App handles. Everything is presentation-layer only
// in Phase 1: guests transact directly with the retailer or handle,
// nothing routes through Yourweddingfriends.
const RETAILER_OPTIONS = [
  { value: 'amazon', label: 'Amazon' },
  { value: 'target', label: 'Target' },
  { value: 'crate_barrel', label: 'Crate & Barrel' },
  { value: 'williams_sonoma', label: 'Williams Sonoma' },
  { value: 'zola', label: 'Zola' },
  { value: 'macys', label: "Macy's" },
  { value: 'custom', label: 'Other / custom' },
];

const CASH_FUND_CATEGORIES = [
  { value: 'honeymoon', label: 'Honeymoon' },
  { value: 'house', label: 'House / home' },
  { value: 'experience', label: 'Experience' },
  { value: 'charity', label: 'Charity' },
  { value: 'custom', label: 'Custom' },
];

export default function Registry() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [slug, setSlug] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      const { data } = await client.get('/registry');
      setCfg(data);
      client.get('/website/config').then((r) => setSlug(r.data?.slug || '')).catch(() => {});
    })();
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const next = { ...cfg, ...patch };
      const { data } = await client.put('/registry', next);
      setCfg(data);
      toast({ title: 'Registry saved' });
    } catch (e) {
      toast({ title: 'Save failed', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) {
    return <div className="p-8 text-[#9A8A7E]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading registry…</div>;
  }

  return (
    <div className="space-y-6" data-testid="registry-editor">
      <header className="flex items-center gap-3">
        <div>
          <h1 className="font-serif text-3xl text-[#2A2A2A] flex items-center gap-3"><AppIcon feature="registry" size="lg" reveal testId="section-icon-registry" />Registry & Gifts</h1>
          <p className="text-sm text-[#6A6A6A]">Aggregate every retailer registry, cash fund, and payment handle in one calm place. Guests can pay into your funds and group gifts by card, and the money goes straight to you. We take no fee.</p>
        </div>
      </header>

      <PayoutSetupCard slug={slug} toast={toast} />

      <section className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
        <h2 className="font-serif text-xl mb-3 inline-flex items-center gap-2"><Link2 className="w-4 h-4" /> Retailer registries</h2>
        {(cfg.external_links || []).map((link, i) => (
          <div key={link.id || i} className="flex items-start gap-2 mb-3" data-testid={`registry-link-${i}`}>
            <select
              value={link.retailer || 'custom'}
              onChange={(e) => {
                const next = [...cfg.external_links];
                next[i] = { ...link, retailer: e.target.value };
                setCfg({ ...cfg, external_links: next });
              }}
              className="text-xs px-2 py-2 rounded-lg border border-[#EFE3D8] bg-white"
            >{RETAILER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
            <input
              value={link.name || ''}
              onChange={(e) => {
                const next = [...cfg.external_links];
                next[i] = { ...link, name: e.target.value };
                setCfg({ ...cfg, external_links: next });
              }}
              placeholder="Display name"
              className="flex-1 min-w-[8rem] px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white"
            />
            <input
              value={link.url || ''}
              onChange={(e) => {
                const next = [...cfg.external_links];
                next[i] = { ...link, url: e.target.value };
                setCfg({ ...cfg, external_links: next });
              }}
              placeholder="https://…"
              className="flex-[2] px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white"
            />
            <button
              data-testid={`registry-link-delete-${i}`}
              onClick={() => save({ external_links: cfg.external_links.filter((_, x) => x !== i) })}
              className="text-[#B8896C] hover:text-[#7B2C3B] p-2"
            ><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <div className="flex items-center gap-3 mt-2">
          <button
            data-testid="registry-add-link"
            onClick={() => setCfg({ ...cfg, external_links: [...(cfg.external_links || []), { name: '', url: '', retailer: 'custom' }] })}
            className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] hover:bg-[#F6EAE0] inline-flex items-center gap-1"
          ><Plus className="w-3 h-3" /> Add registry link</button>
          <button
            data-testid="registry-save-links"
            onClick={() => save({})}
            disabled={saving}
            className="text-xs px-4 py-1.5 rounded-full btn-primary disabled:opacity-50"
          >{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </section>

      <section className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
        <h2 className="font-serif text-xl mb-3 inline-flex items-center gap-2"><Heart className="w-4 h-4" /> Cash funds</h2>
        {(cfg.cash_funds || []).map((fund, i) => (
          <div key={fund.id || i} className="mb-4 p-3 rounded-xl border border-[#EFE3D8] bg-[#FBF7F2]" data-testid={`cash-fund-${i}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <input
                value={fund.title || ''}
                onChange={(e) => {
                  const next = [...cfg.cash_funds];
                  next[i] = { ...fund, title: e.target.value };
                  setCfg({ ...cfg, cash_funds: next });
                }}
                placeholder="Title (e.g. Bali honeymoon)"
                className="px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white"
              />
              <select
                value={fund.category || 'custom'}
                onChange={(e) => {
                  const next = [...cfg.cash_funds];
                  next[i] = { ...fund, category: e.target.value };
                  setCfg({ ...cfg, cash_funds: next });
                }}
                className="px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white"
              >{CASH_FUND_CATEGORIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
            </div>
            <textarea
              value={fund.description || ''}
              onChange={(e) => {
                const next = [...cfg.cash_funds];
                next[i] = { ...fund, description: e.target.value };
                setCfg({ ...cfg, cash_funds: next });
              }}
              placeholder="A short note about this fund…"
              rows={2}
              className="w-full mt-2 px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white"
            />
            <div className="mt-2 max-w-xs">
              <PhotoUploader value={fund.hero_photo} onChange={(url) => { const next = [...cfg.cash_funds]; next[i] = { ...fund, hero_photo: url }; setCfg({ ...cfg, cash_funds: next }); }} testId={`cash-fund-photo-${i}`} label="Fund photo" />
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <input
                type="number"
                value={fund.goal_cents ? fund.goal_cents / 100 : ''}
                onChange={(e) => {
                  const cents = e.target.value ? Math.round(parseFloat(e.target.value) * 100) : null;
                  const next = [...cfg.cash_funds];
                  next[i] = { ...fund, goal_cents: cents };
                  setCfg({ ...cfg, cash_funds: next });
                }}
                placeholder="Goal amount"
                className="px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white"
              />
              <select
                value={fund.currency || 'usd'}
                onChange={(e) => {
                  const next = [...cfg.cash_funds];
                  next[i] = { ...fund, currency: e.target.value };
                  setCfg({ ...cfg, cash_funds: next });
                }}
                className="px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white"
              ><option value="usd">USD</option><option value="cad">CAD</option></select>
            </div>
            <button
              onClick={() => save({ cash_funds: cfg.cash_funds.filter((_, x) => x !== i) })}
              className="mt-2 text-[11px] text-[#B8896C] hover:text-[#7B2C3B] inline-flex items-center gap-1"
              data-testid={`cash-fund-delete-${i}`}
            ><Trash2 className="w-3 h-3" /> Remove this fund</button>
          </div>
        ))}
        <div className="flex items-center gap-3">
          <button
            data-testid="registry-add-fund"
            onClick={() => setCfg({ ...cfg, cash_funds: [...(cfg.cash_funds || []), { title: '', category: 'honeymoon', currency: 'usd' }] })}
            className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] hover:bg-[#F6EAE0] inline-flex items-center gap-1"
          ><Plus className="w-3 h-3" /> Add cash fund</button>
          <button
            data-testid="registry-save-funds"
            onClick={() => save({})}
            disabled={saving}
            className="text-xs px-4 py-1.5 rounded-full btn-primary disabled:opacity-50"
          >{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </section>

      <GroupGiftsEditor cfg={cfg} setCfg={setCfg} save={save} saving={saving} />

      <ContributionsList toast={toast} />

      <section className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
        <h2 className="font-serif text-xl mb-3 inline-flex items-center gap-2"><DollarSign className="w-4 h-4" /> Direct payment handles</h2>
        <p className="text-xs text-[#6A6A6A] mb-3">These are optional. Add only the ones you actively use — they show as tap-to-send links on the public registry page.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {['venmo', 'paypal', 'cashapp', 'zelle'].map((h) => (
            <label key={h} className="text-xs">
              <span className="uppercase tracking-wider text-[#7B2C3B]">{h}</span>
              <input
                data-testid={`registry-handle-${h}`}
                value={cfg.handles?.[h] || ''}
                onChange={(e) => setCfg({ ...cfg, handles: { ...cfg.handles, [h]: e.target.value } })}
                placeholder={h === 'venmo' ? '@your-handle' : (h === 'zelle' ? 'email or phone' : 'your handle')}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white"
              />
            </label>
          ))}
        </div>
        <textarea
          data-testid="registry-intro-note"
          value={cfg.intro_note || ''}
          onChange={(e) => setCfg({ ...cfg, intro_note: e.target.value })}
          placeholder="A short intro note for your guests (optional)…"
          rows={2}
          className="w-full mt-3 px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white"
        />
        <div className="flex items-center gap-3 mt-3">
          <label className="text-xs inline-flex items-center gap-2 cursor-pointer">
            <input
              data-testid="registry-enabled"
              type="checkbox"
              className="accent-[#7B2C3B]"
              checked={cfg.enabled !== false}
              onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
            />
            <span className="text-[#3A3A3A]">Show the registry page on our public wedding site</span>
          </label>
          <button
            data-testid="registry-save-handles"
            onClick={() => save({})}
            disabled={saving}
            className="ml-auto text-xs px-4 py-1.5 rounded-full btn-primary disabled:opacity-50"
          >{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </section>
    </div>
  );
}
